import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Modal, Button, Form, Badge, Row, Col, Alert } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faGear,
  faListCheck,
  faPause,
  faPlay,
  faPlus,
  faRobot,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import { useTimer } from "../context/TimerContext";
import { formatarCronometro, formatarDuracao, segundosDaTarefa } from "../utils/taskTime";
import { estaAtrasada, getResponsavelNome } from "../utils/taskGrouping";
import { getTaskPriorityClass, getTaskPriorityLabel } from "../utils/taskPriority";
import "./TaskKanbanView.css";

// Iniciais para o avatar do responsavel - mesma ideia de qualquer app de
// kanban: sem foto, duas letras já identificam quem é o dono do card.
const iniciaisDoResponsavel = (nome) => {
  if (!nome) return null;
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] || "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase() || null;
};

const formatarDataCurta = (data) =>
  data ? new Date(data).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : null;

// Selo "trabalhando" e so informativo (nao e lock): se o agente travar sem
// nunca chamar avancar/devolver, o selo ficaria grudado pra sempre. Depois
// desse tempo, tratamos como orfao e paramos de mostrar - o servidor so
// limpa de verdade quando a tarefa muda de etapa.
const LIMITE_PROCESSAMENTO_MS = 15 * 60 * 1000;

const agenteProcessandoAgora = (task) => {
  if (!task.pipeline_agente_ativo || !task.pipeline_iniciado_em) return null;
  const decorrido = Date.now() - new Date(task.pipeline_iniciado_em).getTime();
  return decorrido < LIMITE_PROCESSAMENTO_MS ? task.pipeline_agente_ativo : null;
};

// Sugestoes do pipeline de agentes (PRD 17/09/2026). O campo aceita texto
// livre porque a lista de agentes e extensivel - um datalist so poupa digitar
// os nomes de novo, nao trava em quatro valores.
const AGENTES_CONHECIDOS = [
  "analisador_agent",
  "coder_agent",
  "teste_agent",
  "documentador_agent",
];

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) throw new Error(response?.data?.message || "Falha ao carregar dados.");
  return response?.data?.records || response?.data?.data || [];
};

const renderTaskCard = (task, dragProvided, snapshot, tempo, onAbrirTarefa) => {
  const segundosTarefa = segundosDaTarefa(task, tempo.emAndamento);
  const contandoAqui =
    Boolean(tempo.emAndamento) && Number(tempo.emAndamento.id_tarefa) === Number(task.id);

  const atrasada = estaAtrasada(task);
  const nomeResponsavel = getResponsavelNome(task);
  const iniciais = iniciaisDoResponsavel(nomeResponsavel);
  const dataCurta = formatarDataCurta(task.vencimento);

  const totalSubtarefas = Number(task.total_subtarefas) || 0;
  const subtarefasFeitas = Number(task.subtarefas_concluidas) || 0;
  const subtarefasCompletas = totalSubtarefas > 0 && subtarefasFeitas >= totalSubtarefas;

  const agenteEmProcessamento = agenteProcessandoAgora(task);

  const card = (
    <div
      className={`kanban-card ${snapshot.isDragging ? "is-dragging" : ""} ${
        agenteEmProcessamento ? "kanban-card-processando" : ""
      }`}
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      {...dragProvided.dragHandleProps}
      style={dragProvided.draggableProps.style}
      // O quadro nunca teve como abrir o card - so dava pra editar (tags,
      // vez do pipeline, comentarios) pela visao em lista. Clique de mouse
      // nao conflita com o arraste (a lib distingue pelo movimento, nao pelo
      // clique em si). Nao mexe em role/tabIndex/onKeyDown aqui - isso ja
      // vem de dragHandleProps e e o que faz o arraste por teclado (espaco
      // pega, setas movem); sobrescrever quebraria essa acessibilidade.
      onClick={() => onAbrirTarefa?.(task)}
    >
      <div className="kanban-card-content">
        {agenteEmProcessamento && (
          <div className="kanban-card-processando-selo">
            <FontAwesomeIcon icon={faRobot} />
            {agenteEmProcessamento} esta trabalhando nisso
          </div>
        )}

        {task.tags?.length > 0 && (
          <div className="kanban-card-tags">
            {task.tags.map((tag) => (
              <span
                key={tag.id}
                className="kanban-card-tag"
                style={{
                  background: tag.cor_hexa || "#e2e8f0",
                  color: tag.cor_texto_hexa || "#0f172a",
                }}
              >
                {tag.nome}
              </span>
            ))}
          </div>
        )}

        <p className="kanban-card-titulo">
          <span className="kanban-card-numero">#{task.id}</span>
          {task.descricao}
        </p>

        {task.projeto && (
          <div className="kanban-card-projeto">
            <span className="kanban-card-projeto-cor" style={task.cor ? { background: task.cor } : undefined} />
            <span>{task.projeto}</span>
          </div>
        )}

        <div className="kanban-card-rodape">
          <span className={`kanban-pill kanban-pill-prioridade ${getTaskPriorityClass(task.prioridade)}`}>
            {getTaskPriorityLabel(task.prioridade)}
          </span>

          {dataCurta && (
            <span className={`kanban-pill kanban-pill-prazo ${atrasada ? "atrasado" : ""}`}>
              <FontAwesomeIcon icon={faCalendarDay} />
              {dataCurta}
            </span>
          )}

          {totalSubtarefas > 0 && (
            <span className={`kanban-pill kanban-pill-subtarefas ${subtarefasCompletas ? "completo" : ""}`}>
              <FontAwesomeIcon icon={faListCheck} />
              {subtarefasFeitas}/{totalSubtarefas}
            </span>
          )}

          {iniciais && (
            <span className="kanban-avatar" title={nomeResponsavel}>
              {iniciais}
            </span>
          )}
        </div>

        {/* onMouseDown/onClick param param aqui: sem isso o clique no botao
            tambem abriria o card por baixo. */}
        <button
          type="button"
          className={`kanban-card-tempo ${contandoAqui ? "rodando" : ""}`}
          disabled={tempo.ocupado}
          onMouseDown={(evento) => evento.stopPropagation()}
          onClick={(evento) => {
            evento.stopPropagation();
            tempo.alternar(task);
          }}
          title={contandoAqui ? "Pausar cronometro" : "Iniciar cronometro"}
        >
          <FontAwesomeIcon icon={contandoAqui ? faPause : faPlay} />
          {contandoAqui
            ? formatarCronometro(tempo.segundos)
            : segundosTarefa > 0
            ? formatarDuracao(segundosTarefa)
            : "Iniciar"}
        </button>
      </div>
    </div>
  );

  if (snapshot.isDragging) {
    return createPortal(card, document.body);
  }

  return card;
};

const TaskKanbanView = ({ user, workspaceId, tarefas = [], onTaskMoved, onTaskClick }) => {
  const { emAndamento, segundos, ocupado, alternar } = useTimer();
  const [columns, setColumns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [novaEtapa, setNovaEtapa] = useState("");
  const [corEtapa, setCorEtapa] = useState("#e9ecef");
  const [textoCorEtapa, setTextoCorEtapa] = useState("#000000");
  const [etapaConclusao, setEtapaConclusao] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedResponsaveis, setSelectedResponsaveis] = useState([]);
  const [somenteMinhas, setSomenteMinhas] = useState(false);
  const [projectsOptions, setProjectsOptions] = useState([]);
  const [responsaveisOptions, setResponsaveisOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [etapas, setEtapas] = useState([]);

  // Configuracao do pipeline de agentes por etapa (Fase 2 do PRD): qual agente
  // atua na coluna, para onde ela devolve e quantas tentativas aceita antes de
  // so sinalizar em vez de barrar.
  const [showEditModal, setShowEditModal] = useState(false);
  const [etapaEmEdicao, setEtapaEmEdicao] = useState(null);
  const [agenteEdicao, setAgenteEdicao] = useState("");
  const [devolucaoEdicao, setDevolucaoEdicao] = useState("");
  const [proximaEdicao, setProximaEdicao] = useState("");
  const [limiteTentativasEdicao, setLimiteTentativasEdicao] = useState(3);

  const tarefasFiltradas = useMemo(() => {
    let resultado = [...tarefas];

    if (selectedProjects.length > 0) {
      const idsProjetos = selectedProjects.map((project) => String(project.value));
      resultado = resultado.filter((task) => idsProjetos.includes(String(task.id_projeto)));
    }

    if (selectedResponsaveis.length > 0) {
      const idsResponsaveis = selectedResponsaveis.map((responsavel) => String(responsavel.value));
      resultado = resultado.filter((task) =>
        idsResponsaveis.includes(String(task.id_usuario_responsavel))
      );
    }

    if (somenteMinhas && user?.id) {
      resultado = resultado.filter((task) => String(task.id_usuario_responsavel) === String(user.id));
    }

    return resultado;
  }, [tarefas, selectedProjects, selectedResponsaveis, somenteMinhas, user]);

  useEffect(() => {
    if (!workspaceId) {
      setProjectsOptions([]);
      setResponsaveisOptions([]);
      return;
    }
    carregarFiltros();
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      setEtapas([]);
      setColumns([]);
      return;
    }
    carregarEtapas();
  }, [workspaceId]);

  // Reconstrói colunas em memória sempre que etapas ou tarefas filtradas mudam — sem request à API
  useEffect(() => {
    if (etapas.length === 0) return;
    setColumns(
      etapas.map((etapa) => ({
        ...etapa,
        tarefas: tarefasFiltradas.filter(
          (task) =>
            String(task.id_etapa) === String(etapa.id) ||
            (!task.id_etapa && etapa.descricao?.toLowerCase() === "backlog")
        ),
      }))
    );
  }, [etapas, tarefasFiltradas]);

  const carregarFiltros = async () => {
    try {
      const [projetosRes, responsaveisRes] = await Promise.all([
        api.get(`/projects?workspaceId=${workspaceId}`),
        api.get(`/workspaces/${workspaceId}/members`),
      ]);

      const projetos = parseCollection(projetosRes);
      const responsaveis = parseCollection(responsaveisRes);

      setProjectsOptions(
        projetos.map((project) => ({ value: project.id, label: project.descricao }))
      );

      setResponsaveisOptions(
        responsaveis.map((member) => ({
          value: member.id_usuario ?? member.id,
          label: member.descricao || member.nome || member.email,
        }))
      );
    } catch (err) {
      console.error("Erro ao carregar filtros do kanban:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel carregar os filtros do workspace."
      );
    }
  };

  const carregarEtapas = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const etapasResponse = await api.get(`/workspaces/${workspaceId}/kanban/etapas`);
      setEtapas(parseCollection(etapasResponse));
    } catch (err) {
      console.error("Erro ao carregar Kanban:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel carregar as etapas do kanban."
      );
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceColIndex = columns.findIndex((column) => String(column.id) === source.droppableId);
    const destColIndex = columns.findIndex((column) => String(column.id) === destination.droppableId);

    if (sourceColIndex < 0 || destColIndex < 0) return;

    const updatedColumns = columns.map((column) => ({
      ...column,
      tarefas: [...column.tarefas],
    }));

    const [movedTask] = updatedColumns[sourceColIndex].tarefas.splice(source.index, 1);
    updatedColumns[destColIndex].tarefas.splice(destination.index, 0, {
      ...movedTask,
      id_etapa: Number(destination.droppableId),
    });

    setColumns(updatedColumns);

    try {
      await api.patch(`/tasks/${movedTask.id}/etapa`, {
        id_etapa: Number(destination.droppableId),
      });
      onTaskMoved?.(movedTask.id, Number(destination.droppableId));
    } catch (err) {
      console.error("Erro ao atualizar etapa:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel mover a tarefa."
      );
      setColumns(columns);
    }
  };

  const handleNovaEtapa = async () => {
    if (!workspaceId || !novaEtapa.trim()) return;

    try {
      await api.post(`/workspaces/${workspaceId}/kanban/etapas`, {
        descricao: novaEtapa,
        cor_hexa: corEtapa,
        cor_texto_hexa: textoCorEtapa,
        etapa_conclusao: etapaConclusao,
      });

      setNovaEtapa("");
      setCorEtapa("#e9ecef");
      setTextoCorEtapa("#000000");
      setEtapaConclusao(false);
      setShowModal(false);
      carregarEtapas();
    } catch (err) {
      console.error("Erro ao criar etapa:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel criar a etapa."
      );
    }
  };

  const excluirEtapa = async (idEtapa) => {
    if (!window.confirm("Deseja realmente excluir esta etapa?")) return;

    try {
      await api.delete(`/workspaces/${workspaceId}/kanban/etapas/${idEtapa}`);
      carregarEtapas();
    } catch (err) {
      console.error("Erro ao excluir etapa:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel excluir a etapa."
      );
    }
  };

  const abrirEdicaoEtapa = (col) => {
    setEtapaEmEdicao(col);
    setAgenteEdicao(col.agente || "");
    setDevolucaoEdicao(col.id_etapa_devolucao ? String(col.id_etapa_devolucao) : "");
    setProximaEdicao(col.id_etapa_proxima ? String(col.id_etapa_proxima) : "");
    setLimiteTentativasEdicao(col.limite_tentativas || 3);
    setShowEditModal(true);
  };

  const salvarEdicaoEtapa = async () => {
    if (!workspaceId || !etapaEmEdicao) return;

    try {
      // As quatro chaves sempre vao no corpo, mesmo vazias: e o texto vazio
      // que diz "limpar o campo" (fila humana, sem devolucao/proxima
      // configurada), nao a ausencia da chave - o backend distingue os dois
      // casos. Sem id_etapa_proxima, o avancar cai no fallback por "ordem" -
      // por isso so precisa mexer aqui quando a ordem visual das colunas nao
      // for a ordem real do fluxo.
      await api.put(`/workspaces/${workspaceId}/kanban/etapas/${etapaEmEdicao.id}`, {
        agente: agenteEdicao.trim() || null,
        id_etapa_devolucao: devolucaoEdicao || null,
        id_etapa_proxima: proximaEdicao || null,
        limite_tentativas: Number(limiteTentativasEdicao) || 3,
      });

      setShowEditModal(false);
      setEtapaEmEdicao(null);
      carregarEtapas();
    } catch (err) {
      console.error("Erro ao salvar configuracao da etapa:", err);
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Nao foi possivel salvar a configuracao da etapa."
      );
    }
  };

  if (!workspaceId) {
    return <Alert variant="info">Selecione um workspace para visualizar o kanban.</Alert>;
  }

  return (
    <div className="kanban-container">
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <div className="kanban-filters mb-4">
        <Row className="align-items-end">
          <Col md={4}>
            <Form.Label>Projetos</Form.Label>
            <Select
              isMulti
              classNamePrefix="rs"
              options={projectsOptions}
              value={selectedProjects}
              onChange={(value) => setSelectedProjects(value || [])}
              placeholder="Filtrar por projetos..."
            />
          </Col>
          <Col md={4}>
            <Form.Label>Responsaveis</Form.Label>
            <Select
              isMulti
              classNamePrefix="rs"
              options={responsaveisOptions}
              value={selectedResponsaveis}
              onChange={(value) => setSelectedResponsaveis(value || [])}
              placeholder="Filtrar por responsaveis..."
            />
          </Col>
          <Col md={4}>
            <Form.Check
              type="switch"
              id="somenteMinhas"
              label="Somente minhas tarefas"
              checked={somenteMinhas}
              onChange={(e) => setSomenteMinhas(e.target.checked)}
            />
          </Col>
        </Row>
      </div>

      <div className="kanban-header d-flex justify-content-between align-items-center mb-3">
        <h5>
          <Badge bg="primary" className="me-2">
            Kanban
          </Badge>
          Workspace
        </h5>
        <Button size="sm" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Nova Etapa
        </Button>
      </div>

      {loading ? (
        <p className="text-center mt-4">Carregando etapas...</p>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="kanban-columns">
            {columns.map((col) => (
              <Droppable droppableId={String(col.id)} key={col.id}>
                {(provided, snapshotColuna) => (
                  <div
                    className="kanban-column"
                    // A cor cadastrada da etapa vira uma variavel CSS, usada
                    // so na barra de destaque (.kanban-column-topo) - o corpo
                    // da coluna segue o tema (claro/escuro) do app, nao a
                    // cor de fundo pintada por cima.
                    style={col.cor_hexa ? { "--kanban-cor-etapa": col.cor_hexa } : undefined}
                  >
                    <div className="kanban-column-topo" />

                    <div className="kanban-column-header">
                      <div className="kanban-column-titulo">
                        <h6>{col.descricao}</h6>
                        <span className="kanban-column-contagem">{col.tarefas.length}</span>
                        {col.etapa_conclusao && (
                          <Badge bg="success">Final</Badge>
                        )}
                        {col.agente && (
                          <Badge bg="light" text="dark" className="kanban-badge-agente">
                            <FontAwesomeIcon icon={faRobot} className="me-1" />
                            {col.agente}
                          </Badge>
                        )}
                      </div>
                      <div className="kanban-column-actions">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => abrirEdicaoEtapa(col)}
                          title="Configurar etapa"
                        >
                          <FontAwesomeIcon icon={faGear} />
                        </Button>
                        {col.descricao?.toLowerCase() !== "backlog" && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => excluirEtapa(col.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* ref/droppableProps ficam aqui, no container que de fato
                        rola e lista os cards - nao no wrapper externo. A lib
                        usa este elemento pra medir posicao/scroll durante o
                        arraste; um wrapper por fora so pra estilo quebraria
                        essa medicao. */}
                    <div
                      className={`kanban-column-body ${snapshotColuna.isDraggingOver ? "is-over" : ""}`}
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {col.tarefas.map((task, index) => (
                        <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                          {(dragProvided, snapshot) =>
                            renderTaskCard(
                              task,
                              dragProvided,
                              snapshot,
                              { emAndamento, segundos, ocupado, alternar },
                              onTaskClick
                            )
                          }
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {col.tarefas.length === 0 && (
                        <div className="kanban-coluna-vazia">Nenhuma tarefa aqui</div>
                      )}
                    </div>

                    <button type="button" className="kanban-add-card" onClick={() => onTaskClick?.()}>
                      <FontAwesomeIcon icon={faPlus} /> Adicionar cartao
                    </button>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Etapa do Kanban</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Etapa</Form.Label>
              <Form.Control
                type="text"
                value={novaEtapa}
                onChange={(e) => setNovaEtapa(e.target.value)}
                placeholder="Exemplo: Em andamento"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cor da Etapa</Form.Label>
              <Form.Control type="color" value={corEtapa} onChange={(e) => setCorEtapa(e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Cor do Texto</Form.Label>
              <Form.Control
                type="color"
                value={textoCorEtapa}
                onChange={(e) => setTextoCorEtapa(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Etapa de conclusao"
                checked={etapaConclusao}
                onChange={(e) => setEtapaConclusao(e.target.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleNovaEtapa}>
            Salvar Etapa
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Configurar etapa — {etapaEmEdicao?.descricao}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Agente responsavel</Form.Label>
              <Form.Control
                list="kanban-agentes-conhecidos"
                type="text"
                value={agenteEdicao}
                onChange={(e) => setAgenteEdicao(e.target.value)}
                placeholder="Vazio = fila humana, sem agente"
              />
              <datalist id="kanban-agentes-conhecidos">
                {AGENTES_CONHECIDOS.map((nome) => (
                  <option key={nome} value={nome} />
                ))}
              </datalist>
              <Form.Text muted>
                Deixe vazio para uma fila humana (Backlog, Liberados, Tarefas...).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Etapa de devolucao</Form.Label>
              <Form.Select
                value={devolucaoEdicao}
                onChange={(e) => setDevolucaoEdicao(e.target.value)}
              >
                <option value="">Sem devolucao configurada</option>
                {etapas
                  .filter((e) => e.id !== etapaEmEdicao?.id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.descricao}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text muted>
                Para onde a tarefa volta quando este agente devolve (ex.: Teste devolve para Coder).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Proxima etapa</Form.Label>
              <Form.Select
                value={proximaEdicao}
                onChange={(e) => setProximaEdicao(e.target.value)}
              >
                <option value="">Usar a ordem das colunas</option>
                {etapas
                  .filter((e) => e.id !== etapaEmEdicao?.id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.descricao}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text muted>
                Para onde a tarefa avanca quando este agente termina. Deixe em "Usar a
                ordem das colunas" na maioria dos casos - so escolha uma etapa aqui se a
                ordem visual do quadro nao for a ordem real do fluxo.
              </Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>Limite de tentativas</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={limiteTentativasEdicao}
                onChange={(e) => setLimiteTentativasEdicao(e.target.value)}
              />
              <Form.Text muted>
                Depois desse numero de devolucoes, a tarefa so sinaliza o limite estourado — ela
                continua no fluxo normal, ninguem trava a tarefa automaticamente.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={salvarEdicaoEtapa}>
            Salvar Configuracao
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaskKanbanView;
